import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Landing.css';
import DibujarPredio from './DibujarPredio';
import logoHeader from '../assets/logo-header.png';
import logoHeaderHorizontal from '../assets/logo-header-horizontal.png';
import logoHeaderWhite from '../assets/logo-header-white.png';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const API = process.env.REACT_APP_API_URL;

const COLORES = { extremo: '#c53030', alto: '#dd6b20', moderado: '#d69e2e', bajo: '#8fa3a0' };
const EXPLICACIONES = {
  extremo:  'Peligro máximo. Fuego de alta intensidad.',
  alto:     'Situación grave. Monitorear y preparar evacuación.',
  moderado: 'Actividad importante. Mantener vigilancia.',
  bajo:     'Actividad menor. Sin peligro inmediato.',
};
const FUENTES = {
  GOES:  'Satélite GOES · actualiza cada 15 min',
  FIRMS: 'Satélite NASA VIIRS · actualiza cada 3 horas',
  VIIRS: 'Satélite NASA VIIRS · actualiza cada 3 horas',
};

function frpExp(frp) {
  const v = parseFloat(frp);
  if (v >= 50) return 'Intensidad MUY ALTA — incendio de gran escala';
  if (v >= 20) return 'Intensidad ALTA — fuego activo significativo';
  if (v >= 10) return 'Intensidad MEDIA — fuego activo moderado';
  return 'Intensidad BAJA — fuego pequeño o en inicio';
}

const PREDIO_DEMO = { lat: -39.5, lng: -72.0 };
const FOCOS_DEMO = [
  { lat: -38.20, lng: -71.53, nivel: 'alto',     frp: 35.2, fuente: 'GOES',  temp: 28, humedad: 45, viento: 22.4 },
  { lat: -37.82, lng: -72.08, nivel: 'moderado', frp: 27.5, fuente: 'GOES',  temp: 25, humedad: 60, viento: 15.8 },
  { lat: -36.12, lng: -71.76, nivel: 'bajo',     frp: 8.3,  fuente: 'FIRMS', temp: 22, humedad: 75, viento: 8.2  },
  { lat: -33.45, lng: -70.92, nivel: 'extremo',  frp: 62.1, fuente: 'GOES',  temp: 38, humedad: 18, viento: 45.6 },
  { lat: -40.18, lng: -72.05, nivel: 'alto',     frp: 41.8, fuente: 'GOES',  temp: 24, humedad: 52, viento: 28.3 },
  { lat: -35.58, lng: -71.25, nivel: 'moderado', frp: 18.9, fuente: 'FIRMS', temp: 27, humedad: 55, viento: 12.1 },
  { lat: -39.12, lng: -71.68, nivel: 'bajo',     frp: 5.4,  fuente: 'FIRMS', temp: 19, humedad: 82, viento: 6.5  },
];

function haversine(la1, lo1, la2, lo2) {
  const R = 6371;
  const dL = (la2 - la1) * Math.PI / 180;
  const dO = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dL / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dO / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Pricing — un único plan, precio plano ─────────────────────────────────────
// Mismo precio para cualquier propiedad de hasta 8 ha. Con IVA incluido (lo que el
// cliente ve es lo que paga). Debe coincidir con satfire-backend/src/services/pricing.js
const PRECIO_MENSUAL = 13900;   // CLP, IVA incluido — sobre 8 ha: plan Empresa (cotización)

const PAISES = {
  CL: { code: '+56', bandera: '🇨🇱', label: 'Chile',     digitos: 9  },
  AR: { code: '+54', bandera: '🇦🇷', label: 'Argentina', digitos: 10 },
};

function formatTelLocal(raw, pais = 'CL') {
  const max = PAISES[pais]?.digitos || 9;
  const digits = raw.replace(/\D/g, '').slice(0, max);
  if (!digits) return '';
  if (pais === 'CL') {
    if (digits.length <= 1) return digits;
    if (digits.length <= 5) return `${digits[0]} ${digits.slice(1)}`;
    return `${digits[0]} ${digits.slice(1, 5)} ${digits.slice(5)}`;
  }
  // Argentina: XX XXXX XXXX
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
}

const PALABRAS = ['parcela', 'terreno', 'fundo', 'instalaciones'];

const SEGMENTOS = [
  {
    icon: '🏡', titulo: 'Parcelas y fundos',
    dolor: 'Entérate antes de que el fuego llegue a tu cerco',
    desc: 'Cada temporada hay propietarios que pierden animales, construcciones y años de trabajo porque el incendio llegó sin aviso. Montaraz te da los minutos que necesitas para proteger lo tuyo — y los bosques que te rodean.',
    cta: 'Suscribirme', accion: 'registro',
  },
  {
    icon: '🌲', titulo: 'Empresas forestales',
    dolor: 'Cada minuto cuenta cuando se trata de millones en madera',
    desc: 'Un foco en zona de plantaciones puede destruir en horas lo que tardó décadas en crecer. La detección temprana no es un costo — es protección patrimonial.',
    cta: 'Cotizar', accion: 'empresa',
  },
  {
    icon: '🏛️', titulo: 'Municipalidades',
    dolor: 'Gestiona la emergencia antes de que sea noticia',
    desc: 'La responsabilidad política ante un incendio en tu comuna es real. Montaraz te da visibilidad en tiempo real para activar protocolos antes de que lleguen los periodistas.',
    cta: 'Agendar reunión', accion: 'empresa',
  },
  {
    icon: '🏨', titulo: 'Hotelería y turismo rural',
    dolor: 'Tus huéspedes confían en que los mantendrás seguros',
    desc: 'Un establecimiento en zona de riesgo sin sistema de alerta temprana es un pasivo. Uno que sí lo tiene es un diferenciador con el que comunicas seguridad.',
    cta: 'Cotizar', accion: 'empresa',
  },
  {
    icon: '⛏️', titulo: 'Minería e industria',
    dolor: 'Continuidad operacional cuando más importa',
    desc: 'Un incendio en el entorno de una faena puede paralizar operaciones y comprometer la seguridad del personal. La alerta temprana te da tiempo para activar el protocolo.',
    cta: 'Cotizar', accion: 'empresa',
  },
  {
    icon: '🏗️', titulo: 'Inmobiliarias',
    dolor: '"Este proyecto incluye monitoreo satelital de incendios"',
    desc: 'En zonas de interfaz urbano-rural, ofrecer detección temprana como parte del proyecto es un argumento de venta que casi nadie tiene todavía.',
    cta: 'Hablemos', accion: 'empresa',
  },
];

const FAQS = [
  {
    q: '¿Necesito instalar algo?',
    a: 'No. Montaraz usa satélites que ya están en órbita. Solo registras tu terreno y recibes alertas por WhatsApp. Sin sensores, sin cámaras, sin instalación.',
  },
  {
    q: '¿Cuánto cuesta y cómo se paga?',
    a: 'Un solo plan: $13.900 al mes, IVA incluido, el mismo precio para cualquier parcela o fundo de hasta 8 hectáreas. Se renueva automáticamente cada mes y lo cancelas cuando quieras desde tu perfil, sin permanencia ni multas. Para propiedades más grandes preparamos un plan a medida — escríbenos a contacto@montaraz.cl.',
  },
  {
    q: '¿Qué tan rápida es la alerta?',
    a: 'GOES-16 escanea Sudamérica cada 15 minutos. Cuando detecta un foco, Montaraz verifica la distancia a tus zonas y te notifica en minutos. FIRMS de NASA actualiza cada 3 horas como segunda fuente.',
  },
  {
    q: '¿Funciona de noche o con nubes?',
    a: 'De noche, sí — los satélites detectan calor infrarrojo, no luz visible. Con nubes muy densas la cobertura puede reducirse. No prometemos detección perfecta en esas condiciones, pero en la mayoría de los eventos reales la detección ocurre antes de que el incendio sea visible desde el suelo.',
  },
  {
    q: '¿Qué información trae la alerta?',
    a: 'Recibes por WhatsApp: distancia del foco a tu terreno, nivel de riesgo (bajo/moderado/alto/extremo), intensidad del fuego, condiciones de viento y humedad, y los números de emergencia: Bomberos 132, CONAF 130, Carabineros 133.',
  },
  {
    q: '¿Puedo monitorear varias propiedades?',
    a: 'El plan self-service cubre un predio de hasta 8 ha, con hasta 3 números de WhatsApp recibiendo la alerta. Si necesitas monitorear varias propiedades, escríbenos a contacto@montaraz.cl y preparamos un plan Empresa a medida, sin límite práctico de zonas.',
  },
  {
    q: '¿Cómo contrata una empresa o municipio?',
    a: 'A través de nuestro formulario de contacto. Un ejecutivo se comunica en menos de 24 horas con una propuesta según tu caso: superficie, zonas, integraciones y cobertura requerida.',
  },
];

export default function Landing({ onLogin, authInicial, autoOpenTerrain }) {
  const [modalOpen, setModalOpen]   = useState(false);
  const [tab, setTab]               = useState('login');
  const [form, setForm]             = useState({ nombre: '', email: '', password: '' });
  const [error, setError]           = useState('');
  const [cargando, setCargando]     = useState(false);
  const [authOk, setAuthOk]         = useState(false);
  const [recuperarEmail, setRecuperarEmail] = useState('');
  const [recuperarOk, setRecuperarOk]       = useState(false);
  const [regAceptaTerminos, setRegAceptaTerminos] = useState(false);

  // ── Selector tipo cuenta ──
  const [tipoCuenta, setTipoCuenta] = useState(null); // null | 'personal' | 'comunal' | 'empresa'
  const [empresaForm, setEmpresaForm] = useState({ razon: '', contacto: '', email: '', telefono: '', ha: '', pagina_web: '' });
  const [empresaEnviado, setEmpresaEnviado] = useState(false);

  // ── Flujo multi-paso ──
  const [paso, setPaso]             = useState('auth'); // 'auth' | 'terreno'
  const [authToken, setAuthToken]   = useState(null);
  const [authUsuario, setAuthUsuario] = useState(null);
  // Step 2 – terreno
  const [pNombre, setPNombre]       = useState('');
  const [pPais, setPPais]           = useState('CL');
  const [pTel, setPTel]             = useState('');
  const [pPoligono, setPPoligono]   = useState(null);   // [[lng,lat],...] dibujado en el mapa
  const [pSuperaMax, setPSuperaMax] = useState(false);  // el contorno supera 8 ha
  const [pAceptaTerminos, setPAceptaTerminos] = useState(false);
  const [showTerminos, setShowTerminos]       = useState(false);
  const [ha, setHa]                 = useState('');
  const [popup, setPopup]           = useState(null);

  const [twIdx, setTwIdx]           = useState(0);
  const [twText, setTwText]         = useState('');
  const [twDeleting, setTwDeleting] = useState(false);

  // ── Formulario de contacto ──
  const [ctForm, setCtForm]         = useState({ nombre: '', email: '', telefono: '', mensaje: '', tipo: '', pagina_web: '' });
  const [ctEnviado, setCtEnviado]   = useState(false);
  const [ctCargando, setCtCargando] = useState(false);
  const [ctError, setCtError]       = useState('');

  const mapRef   = useRef(null);
  const mapInst  = useRef(null);
  const modalRef = useRef(null);       // focus trap
  const lastFocusRef = useRef(null);   // restores focus when modal closes

  // Evitar scroll restoration automático del browser
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    return () => {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    };
  }, []);

  // Scroll reveal — activa .is-visible al entrar en viewport
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.13, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.l-reveal, .l-impacto-stat').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const word = PALABRAS[twIdx];
    let t;
    if (!twDeleting && twText.length < word.length) {
      t = setTimeout(() => setTwText(word.slice(0, twText.length + 1)), 110);
    } else if (!twDeleting && twText.length === word.length) {
      t = setTimeout(() => setTwDeleting(true), 1800);
    } else if (twDeleting && twText.length > 0) {
      t = setTimeout(() => setTwText(twText.slice(0, -1)), 65);
    } else {
      setTwDeleting(false);
      setTwIdx((twIdx + 1) % PALABRAS.length);
    }
    return () => clearTimeout(t);
  }, [twText, twDeleting, twIdx]);

  useEffect(() => {
    const mapEl = mapRef.current;
    if (!mapEl) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || mapInst.current) return;
      observer.disconnect();

      // Parchear focus para evitar que Leaflet scrollee al mapa al inicializar
      const origFocus = mapEl.focus.bind(mapEl);
      mapEl.focus = (opts) => origFocus({ ...opts, preventScroll: true });

      const map = L.map(mapEl, { zoomControl: true, tap: false }).setView([-36.5, -71.0], 6);

      mapEl.focus = origFocus; // restaurar

      L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
          subdomains: 'abc',
        }
      ).addTo(map);

      const predioIcon = L.divIcon({
        className: '',
        html: '<img src="/favicon.png" style="width:36px;height:36px;filter:drop-shadow(0 2px 6px rgba(0,0,0,.5))" />',
        iconSize: [36, 36], iconAnchor: [18, 36],
      });

      L.marker([PREDIO_DEMO.lat, PREDIO_DEMO.lng], { icon: predioIcon })
        .addTo(map)
        .bindTooltip('<b>Fundo demo</b>');

      L.circle([PREDIO_DEMO.lat, PREDIO_DEMO.lng], {
        radius: 15000, color: '#1a8a60', weight: 2, dashArray: '6 4',
        opacity: 0.9, fillColor: '#1a8a60', fillOpacity: 0.07,
      }).addTo(map);

      FOCOS_DEMO.forEach(f => {
        const color = COLORES[f.nivel] || '#8fa3a0';
        L.circleMarker([f.lat, f.lng], {
          radius: 7, color: 'rgba(255,255,255,.7)', weight: 1.5,
          fillColor: color, fillOpacity: 1,
        }).addTo(map).on('click', e => {
          L.DomEvent.stopPropagation(e);
          setPopup(f);
        });
      });

      map.on('click', () => setPopup(null));
      mapInst.current = map;
    }, { rootMargin: '0px', threshold: 0.01 });

    observer.observe(mapEl);
    return () => {
      observer.disconnect();
      if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; }
    };
  }, []);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') closeModal(); };
    if (modalOpen) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [modalOpen]);

  useEffect(() => {
    if (autoOpenTerrain && authInicial) {
      setAuthToken(authInicial.token);
      setAuthUsuario(authInicial.usuario);
      setTab('registro');
      setTipoCuenta('personal');
      setPaso('terreno');
      setModalOpen(true);
      document.body.style.overflow = 'hidden';
    }
  }, []);

  useEffect(() => () => { document.body.style.overflow = ''; }, []);

  const openModal = (t, haCtx = '', tcInicial = null) => {
    setTab(t); setError(''); setAuthOk(false);
    setRecuperarOk(false); setRecuperarEmail('');
    setForm({ nombre: '', email: '', password: '' });
    setPaso('auth');
    setPNombre(''); setPPais('CL'); setPTel('');
    setPPoligono(null); setPSuperaMax(false);
    setPAceptaTerminos(false); setRegAceptaTerminos(false);
    setEmpresaEnviado(false);
    setEmpresaForm({ razon: '', contacto: '', email: '', telefono: '', ha: haCtx });
    if (t === 'registro') {
      setTipoCuenta(tcInicial); // null = mostrar selector, 'empresa' = ir directo al form empresa
    } else {
      setTipoCuenta('personal'); // login va directo
    }
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleRecuperar = async (e) => {
    e.preventDefault();
    setError(''); setCargando(true);
    try {
      await fetch(`${API}/api/auth/recuperar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recuperarEmail }),
      });
      setRecuperarOk(true);
    } catch { setError('Error de conexión'); }
    finally { setCargando(false); }
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = '';
    // Devolver el foco al elemento que abrió el modal (WCAG 2.1 SC 2.4.3)
    lastFocusRef.current?.focus();
  };

  // Focus trap: WCAG 2.1 SC 2.1.2 — el foco nunca debe escapar del modal
  useEffect(() => {
    if (!modalOpen) return;
    // Guardar el elemento activo antes de abrir
    lastFocusRef.current = document.activeElement;

    const modal = modalRef.current;
    if (!modal) return;

    // Enfocar el primer elemento interactivo dentro del modal
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(modal.querySelectorAll(FOCUSABLE));
    const firstEl = getFocusable()[0];
    firstEl?.focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };

    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tab === 'registro' && !regAceptaTerminos) {
      return setError('Debes aceptar los Términos y Condiciones para crear tu cuenta.');
    }
    let telLimpioRegistro;
    if (tab === 'registro') {
      const digits = pTel.replace(/\D/g, '');
      const min = PAISES[pPais]?.digitos || 9;
      if (digits.length < min) return setError(`Teléfono inválido — ingresa ${min} dígitos después del ${PAISES[pPais].code}`);
      telLimpioRegistro = `${PAISES[pPais].code}${digits}`;
    }
    setError(''); setCargando(true);
    try {
      const url = tab === 'login' ? `${API}/api/auth/login` : `${API}/api/auth/registro`;
      const body = tab === 'login'
        ? { email: form.email, password: form.password }
        : { nombre: form.nombre, email: form.email, password: form.password, telefono: telLimpioRegistro, tipo_cuenta: tipoCuenta === 'comunal' ? 'comunal' : 'personal', consentimiento: regAceptaTerminos };
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Error al autenticar');
      if (tab === 'login') {
        // Login: ir directo al panel
        localStorage.setItem('satfire_token', data.token);
        localStorage.setItem('satfire_usuario', JSON.stringify(data.usuario));
        setAuthOk(true);
        document.body.style.overflow = '';
        setTimeout(() => onLogin(data.usuario, data.token), 1000);
      } else {
        // Registro: guardar auth y avanzar al paso 2 (terreno)
        setAuthToken(data.token);
        setAuthUsuario(data.usuario);
        setPaso('terreno');
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  const handleCrearPredio = async () => {
    if (!pNombre.trim()) return setError('Ingresa un nombre para tu terreno.');
    if (!pPoligono) return setError('Dibuja el contorno de tu terreno en el mapa (al menos 3 esquinas).');
    if (pSuperaMax) return setError(tipoCuenta === 'comunal'
      ? 'Tu zona supera las 50 ha — escríbenos a contacto@montaraz.cl para un plan a medida.'
      : 'Tu propiedad supera las 8 ha — escríbenos a contacto@montaraz.cl para un plan a medida.');
    if (pTel) {
      const digits = pTel.replace(/\D/g, '');
      const min = PAISES[pPais]?.digitos || 9;
      if (digits.length < min) return setError(`Teléfono inválido — ingresa ${min} dígitos después del ${PAISES[pPais].code}`);
    }
    if (!pAceptaTerminos) return setError('Debes aceptar los Términos y Condiciones para continuar.');
    setCargando(true); setError('');
    try {
      const telDigits = pTel ? pTel.replace(/\D/g, '') : null;
      const telLimpio = telDigits ? `${PAISES[pPais].code}${telDigits}` : undefined;
      const res = await fetch(`${API}/api/cliente/predios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + authToken },
        body: JSON.stringify({ nombre: pNombre, poligono: pPoligono, radio_alerta_km: 15, telefono: telLimpio }),
      });
      const data = await res.json();
      if (!data.ok) return setError(data.error || 'Error al crear terreno');
      localStorage.setItem('satfire_token', authToken);
      localStorage.setItem('satfire_usuario', JSON.stringify(authUsuario));
      document.body.style.overflow = '';
      onLogin(authUsuario, authToken);
    } catch { setError('Error de conexión'); }
    finally { setCargando(false); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleContacto = async (e) => {
    e.preventDefault();
    if (!ctForm.nombre || !ctForm.email || !ctForm.mensaje) return setCtError('Completa nombre, email y mensaje.');
    setCtCargando(true); setCtError('');
    try {
      const res = await fetch(`${API}/api/auth/contacto`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ctForm),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Error al enviar');
      setCtEnviado(true);
    } catch (err) { setCtError('Error de conexión. Escríbenos a contacto@montaraz.cl'); }
    finally { setCtCargando(false); }
  };

  const focoInfo = popup ? (() => {
    const dist = haversine(popup.lat, popup.lng, PREDIO_DEMO.lat, PREDIO_DEMO.lng);
    return {
      ...popup,
      dist,
      color: COLORES[popup.nivel] || '#8fa3a0',
      distColor: dist < 10 ? '#c53030' : dist < 15 ? '#dd6b20' : '#1a8a60',
    };
  })() : null;

  // Artículo correcto según la palabra actual del typewriter
  const articulo = PALABRAS[twIdx] === 'instalaciones' ? 'tus' : 'tu';

  return (
    <div className="landing">

      {/* ── NAV — glass floating pill ── */}
      <nav className="l-nav">
        <div className="l-nav-shell">
          <div className="l-nav-left">
            <img src={logoHeader} alt="Montaraz" className="l-nav-logo-img" />
            <span className="l-nav-badge"><span className="live-dot" />En vivo</span>
          </div>
          <div className="l-nav-right">
            <button className="l-nav-link" onClick={() => scrollTo('l-contacto')}>Contacto</button>
            <button className="l-btn l-btn-ghost l-btn-pill" onClick={() => openModal('login')}>Ingresar</button>
            <button className="l-btn l-btn-primary l-btn-pill" onClick={() => openModal('registro')}>Registrarse</button>
          </div>
        </div>
      </nav>

      {/* ── HERO — panel insertado bosque esmeralda ── */}
      <section className="l-hero">
        <img src={logoHeaderWhite} alt="Montaraz" className="l-hero-logo" />
        <div className="l-eyebrow">Guardián del Territorio</div>
        <div className="l-product-pill">🛰️ Alerta temprana de incendios forestales · por satélite</div>
        <h1 className="l-h1">
          Duerme tranquilo.{' '}
          <span className="l-highlight">
            Alguien cuida {articulo} <span className="l-tw">{twText}</span><span className="l-cursor">|</span>
          </span>
        </h1>
        <p className="l-hero-sub">
          Dos satélites monitorean tu zona las 24 horas. Cuando detectan un foco de incendio cerca de tu terreno, te avisamos por WhatsApp en minutos — para que llegues antes que el fuego.
        </p>
        <div className="l-hero-cta">
          <button className="l-btn l-btn-primary l-btn-pill l-btn-lg" onClick={() => openModal('registro')}>
            Proteger mi terreno
          </button>
          <button className="l-btn l-btn-ghost l-btn-pill l-btn-lg" onClick={() => openModal('registro', '', 'empresa')}>
            Soy empresa o institución →
          </button>
        </div>
        <div className="l-stats">
          <div className="l-stat"><div className="l-stat-n">15 km</div><div className="l-stat-l">Radio de alerta</div></div>
          <div className="l-stat"><div className="l-stat-n">~15 min</div><div className="l-stat-l">Detección GOES</div></div>
          <div className="l-stat"><div className="l-stat-n">24/7</div><div className="l-stat-l">Monitoreo</div></div>
          <div className="l-stat l-stat-nasa">
            <img src="https://upload.wikimedia.org/wikipedia/commons/e/e5/NASA_logo.svg" alt="NASA" className="l-nasa-logo" />
            <div className="l-stat-l">Datos FIRMS</div>
          </div>
        </div>

        {/* Íconos flotantes decorativos */}
        <div className="l-hero-floats" aria-hidden="true">
          <span className="l-hero-float">🛰️</span>
          <span className="l-hero-float">🔥</span>
          <span className="l-hero-float">📲</span>
          <span className="l-sparkle l-sparkle--md"></span>
          <span className="l-sparkle l-sparkle--sm"></span>
          <span className="l-sparkle l-sparkle--md"></span>
        </div>
      </section>

      {/* ── EL PROBLEMA ── */}
      <section className="l-section l-problema">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2>Un foco pequeño se controla. Un incendio declarado, no.</h2>
            <p>La diferencia entre los dos la marca el tiempo de respuesta. Ese tiempo, Montaraz te lo da.</p>
          </div>
          <div className="l-problema-grid">
            <div className="l-problema-card l-reveal">
              <div className="l-problema-icon">🔥</div>
              <div className="l-problema-titulo">Los incendios no avisan</div>
              <p>Cada verano miles de hectáreas arden en Chile. La mayoría de esos incendios empezaron como un foco pequeño que nadie detectó a tiempo.</p>
            </div>
            <div className="l-problema-card l-problema-card--accent l-reveal l-reveal--d1">
              <div className="l-problema-icon">⏱️</div>
              <div className="l-problema-titulo">Minutos que lo cambian todo</div>
              <p>Cuando ves el humo desde tu ventana, el foco ya lleva tiempo activo. Un aviso 30 minutos antes puede ser la diferencia entre salvar y perder.</p>
            </div>
            <div className="l-problema-card l-reveal l-reveal--d2">
              <div className="l-problema-icon">📡</div>
              <div className="l-problema-titulo">Los satélites ya lo ven</div>
              <p>Los mismos satélites que usan los bomberos y la CONAF están en órbita ahora mismo. Montaraz los conecta directamente a tu WhatsApp.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section className="l-section l-como">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2>Así funciona</h2>
            <p>Sin sensores. Sin técnicos. En menos de 5 minutos tu terreno queda bajo vigilancia satelital.</p>
          </div>
          <div className="l-steps">
            <div className="l-step l-reveal">
              <div className="l-step-icon">📍</div>
              <h3>1. Registras tu terreno</h3>
              <p>Marcas tu propiedad en el mapa, defines el radio de alerta y agregas los números de WhatsApp que deben recibir las alertas. Listo.</p>
            </div>
            <div className="l-step l-reveal l-reveal--d1">
              <div className="l-step-icon">🛰️</div>
              <h3>2. Los satélites escanean</h3>
              <p>GOES-16 escanea tu zona cada 15 minutos. NASA FIRMS cada 3 horas como segunda fuente. Dos sistemas independientes, cero puntos ciegos.</p>
            </div>
            <div className="l-step l-reveal l-reveal--d2">
              <div className="l-step-icon">📲</div>
              <h3>3. Recibes la alerta</h3>
              <p>Llega un WhatsApp con la distancia exacta al foco, nivel de riesgo, condiciones de viento y humedad, y los números de emergencia: Bomberos 132, CONAF 130.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEMO UNIFICADA ── */}
      <section className="l-section l-demo-unificada" id="l-demo">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2 style={{ color: '#f0ece0' }}>Así se ve Montaraz por dentro</h2>
            <p style={{ color: 'rgba(240,236,224,.65)' }}>Dashboard en tiempo real · Alertas instantáneas vía WhatsApp</p>
          </div>
          <div>

            {/* ── Panel real del app ── */}
            <div className="l-browser-mockup">
              {/* Chrome del browser */}
              <div className="l-browser-bar">
                <div className="l-browser-dots">
                  <div className="l-browser-dot l-browser-dot--r" />
                  <div className="l-browser-dot l-browser-dot--y" />
                  <div className="l-browser-dot l-browser-dot--g" />
                </div>
                <div className="l-browser-url">montaraz.cl/panel</div>
              </div>
              {/* App: navbar real */}
              <div className="l-app-navbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <img src={logoHeader} alt="Montaraz" style={{ height: 28, objectFit: 'contain' }} />
                  <span className="l-app-live-badge">● En vivo</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span className="l-app-nav-btn">Mapa global</span>
                  <span className="l-app-nav-txt">Hola, Carlos</span>
                  <span className="l-app-plan-badge">cliente</span>
                  <span className="l-app-nav-btn">Mi perfil</span>
                </div>
              </div>
              {/* App: contenido principal 2 cols */}
              <div className="l-app-body">
                {/* Col izquierda: selector predio + mapa */}
                <div className="l-app-left">
                  {/* Selector predio */}
                  <div className="l-app-predio-bar">
                    <span className="l-app-predio-pill l-app-predio-pill--active">Fundo Los Boldos</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 3, alignItems: 'center' }}>
                      <span style={{ fontSize: 9, color: '#8b7355' }}>Radio:</span>
                      {['5','10','20'].map(r => (
                        <span key={r} className={`l-app-radio-btn${r === '10' ? ' l-app-radio-btn--active' : ''}`}>{r}</span>
                      ))}
                      <span style={{ fontSize: 9, color: '#8b7355', marginLeft: 2 }}>2 focos</span>
                    </div>
                  </div>
                  {/* Mapa real Leaflet */}
                  <div className="l-app-mapa-real-wrap">
                    <div ref={mapRef} className="l-app-mapa-real" />
                    {focoInfo && (
                      <div className="l-popup l-popup--sm" style={{ borderColor: focoInfo.color }}>
                        <div className="l-popup-header">
                          <div>
                            <div className="l-popup-nivel" style={{ color: focoInfo.color }}>{focoInfo.nivel.toUpperCase()}</div>
                            <div className="l-popup-desc">{EXPLICACIONES[focoInfo.nivel]}</div>
                          </div>
                          <button className="l-popup-close" aria-label="Cerrar" onClick={() => setPopup(null)}>×</button>
                        </div>
                        <div className="l-popup-body">
                          <div className="l-popup-row">
                            <div className="l-popup-row-main">FRP: {focoInfo.frp.toFixed(1)} MW · {focoInfo.fuente}</div>
                            <div className="l-popup-row-sub">{frpExp(focoInfo.frp)}</div>
                          </div>
                          <div className="l-popup-row-info">Temp: {focoInfo.temp}°C · Humedad: {focoInfo.humedad}% · Viento: {focoInfo.viento} km/h</div>
                          <a href={`https://www.google.com/maps?q=${focoInfo.lat},${focoInfo.lng}`} className="l-popup-maps-btn" target="_blank" rel="noopener noreferrer" style={{ background: focoInfo.color }}>Ver en Google Maps</a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Col derecha: cards */}
                <div className="l-app-right">
                  {/* Estado del predio */}
                  <div className="l-app-card l-app-card--alto">
                    <div className="l-app-card-label">Estado del predio</div>
                    <div className="l-app-card-nombre">Fundo Los Boldos</div>
                    <div className="l-app-nivel-alto">ALTO</div>
                    <div className="l-app-nivel-sub">Foco más cercano a 8.3 km</div>
                    <div className="l-app-data-grid">
                      <div className="l-app-data-cell"><div className="l-app-data-lbl">Intensidad</div><div className="l-app-data-val">35 MW</div></div>
                      <div className="l-app-data-cell"><div className="l-app-data-lbl">Fuente</div><div className="l-app-data-val">GOES</div></div>
                      <div className="l-app-data-cell"><div className="l-app-data-lbl">Temperatura</div><div className="l-app-data-val">28°C</div></div>
                      <div className="l-app-data-cell"><div className="l-app-data-lbl">Viento</div><div className="l-app-data-val">22 km/h</div></div>
                    </div>
                  </div>
                  {/* Situación nacional */}
                  <div className="l-app-card">
                    <div className="l-app-card-label">Situación nacional</div>
                    <div className="l-app-stat2-grid">
                      <div className="l-app-stat2"><div className="l-app-stat2-n" style={{ color: '#15704f' }}>14</div><div className="l-app-stat2-l">Focos 24h</div></div>
                      <div className="l-app-stat2"><div className="l-app-stat2-n" style={{ color: '#c53030' }}>2</div><div className="l-app-stat2-l">Extremos</div></div>
                      <div className="l-app-stat2"><div className="l-app-stat2-n" style={{ color: '#dd6b20' }}>5</div><div className="l-app-stat2-l">Altos</div></div>
                      <div className="l-app-stat2"><div className="l-app-stat2-n" style={{ color: '#8b7355' }}>847</div><div className="l-app-stat2-l">Histórico</div></div>
                    </div>
                  </div>
                  {/* Mis alertas */}
                  <div className="l-app-card">
                    <div className="l-app-card-label">Mis alertas</div>
                    <div className="l-app-alerta2" style={{ borderColor: '#dd6b20', background: '#fff8f0' }}>
                      <div style={{ fontWeight: 700, color: '#c05621', fontSize: 9 }}>ALTO — Fundo Los Boldos</div>
                      <div style={{ fontSize: 9, color: '#8b7355', marginTop: 1 }}>8.3 km · 35 MW · 09:14 · WA enviado</div>
                    </div>
                    <div className="l-app-alerta2" style={{ borderColor: '#d69e2e', background: '#f5faee' }}>
                      <div style={{ fontWeight: 700, color: '#b7791f', fontSize: 9 }}>MODERADO — Fundo Los Boldos</div>
                      <div style={{ fontSize: 9, color: '#8b7355', marginTop: 1 }}>22.1 km · 18 MW · 08:31</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ── Parte 2: iPhone grande + mensaje detallado ── */}
          <div className="l-demo-wa-grid">

          {/* iPhone grande */}
          <div className="l-iphone-wrap">
            <div className="l-iphone l-iphone--lg">
              <div className="l-iphone-vol-up" />
              <div className="l-iphone-vol-dn" />
              <div className="l-iphone-power" />
              <div className="l-iphone-screen l-iphone-screen--lg">
                <div className="l-iphone-status">
                  <span>9:41</span>
                  <div className="l-iphone-island" />
                  <span style={{ display: 'flex', gap: 3, alignItems: 'center', fontSize: 8 }}>
                    <span>●●●</span><span>WiFi</span><span>▪</span>
                  </span>
                </div>
                <div className="l-wa-header">
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', marginRight: 2 }}>‹</div>
                  <div className="l-wa-header-avatar">🛰️</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '.85rem' }}>Montaraz Alertas</div>
                    <div style={{ fontSize: '.65rem', opacity: .75 }}>en línea</div>
                  </div>
                  <div style={{ fontSize: 12, opacity: .7 }}>⋮</div>
                </div>
                <div className="l-wa-chat-bg">
                  <div className="l-wa-bubble l-wa-bubble--old">
                    ✅ Predio <strong>Fundo Los Boldos</strong> activado. Monitoreo 24/7 iniciado.
                    <div className="l-wa-time">08:00 ✓✓</div>
                  </div>
                  <div className="l-wa-bubble l-wa-bubble--lg">
                    <div className="l-wa-nivel">🔴 <strong>ALERTA MONTARAZ — NIVEL ALTO</strong></div>
                    <div style={{ margin: '5px 0' }}>Foco detectado a <strong>8.3 km</strong> de tu predio <strong>Fundo Los Boldos</strong>.</div>
                    <div style={{ color: '#c05621', marginBottom: 5 }}>Situación grave. Prepárate para evacuar.</div>
                    <div className="l-wa-divider" />
                    <div className="l-wa-seccion">📊 Datos del foco</div>
                    <div className="l-wa-data-row"><span>Intensidad</span><strong>35 MW</strong></div>
                    <div className="l-wa-data-row"><span>Temperatura</span><strong>28°C</strong></div>
                    <div className="l-wa-data-row"><span>Humedad</span><strong>45%</strong></div>
                    <div className="l-wa-data-row"><span>Viento</span><strong>22 km/h</strong></div>
                    <div className="l-wa-divider" />
                    <div className="l-wa-seccion">⚠️ Qué hacer ahora</div>
                    <div style={{ color: '#444', marginBottom: 2 }}>· Evacúe por rutas seguras alejadas del humo</div>
                    <div style={{ color: '#444', marginBottom: 2 }}>· Alerte a vecinos y familiares</div>
                    <div className="l-wa-divider" />
                    <div className="l-wa-seccion">🚒 Emergencias</div>
                    <div style={{ color: '#444', marginBottom: 2 }}>Bomberos: <strong>132</strong> · CONAF: <strong>130</strong> · Carabineros: <strong>133</strong></div>
                    <div style={{ color: '#aaa', fontSize: '.6rem', fontStyle: 'italic', marginTop: 4 }}>Montaraz · Guardián del territorio</div>
                    <div className="l-wa-time">09:14 ✓✓</div>
                  </div>
                </div>
                <div className="l-wa-input-bar">
                  <div className="l-wa-input-field">Escribe un mensaje...</div>
                  <div className="l-wa-send-btn">➤</div>
                </div>
              </div>
            </div>
            <div className="l-wa-badge-real">✓ Alerta real · vía WhatsApp</div>
          </div>

          {/* Mensaje detallado */}
          <div className="l-wa-detalle">
            <div className="l-wa-detalle-header">
              <span className="l-wa-detalle-chip">Mensaje real que recibes</span>
              <h3 className="l-wa-detalle-titulo">Alerta satelital directa a tu WhatsApp</h3>
              <p className="l-wa-detalle-sub">Así se ve el mensaje completo cuando se detecta un foco cerca de tu predio. Sin apps adicionales — llega directo a tu celular.</p>
            </div>
            <div className="l-wa-detalle-msg">
              <div className="l-wa-detalle-nivel">🔴 ALERTA MONTARAZ — NIVEL ALTO</div>
              <p className="l-wa-detalle-p">Se detectó un foco de incendio a <strong>8.3 km</strong> de tu predio <strong>Fundo Los Boldos</strong>.</p>
              <p className="l-wa-detalle-p" style={{ color: '#c05621', fontWeight: 600 }}>Situación grave. Prepárate para evacuar.</p>
              <div className="l-wa-detalle-hr" />
              <div className="l-wa-detalle-section">📊 Datos del foco</div>
              <div className="l-wa-detalle-fila"><span>Intensidad</span><strong>35 MW — alta</strong></div>
              <div className="l-wa-detalle-fila"><span>Fuente</span><strong>Satélite GOES (15 min)</strong></div>
              <div className="l-wa-detalle-fila"><span>Temperatura</span><strong>28°C</strong></div>
              <div className="l-wa-detalle-fila"><span>Humedad</span><strong>45%</strong></div>
              <div className="l-wa-detalle-fila"><span>Viento</span><strong>22 km/h</strong></div>
              <div className="l-wa-detalle-hr" />
              <div className="l-wa-detalle-section">⚠️ Qué hacer ahora</div>
              <div className="l-wa-detalle-item">· Evacúe por rutas seguras alejadas del humo</div>
              <div className="l-wa-detalle-item">· Alerte a vecinos y familiares</div>
              <div className="l-wa-detalle-item">· No regrese hasta que sea seguro</div>
              <div className="l-wa-detalle-hr" />
              <div className="l-wa-detalle-section">🚒 Emergencias</div>
              <div className="l-wa-detalle-emerg">
                <span className="l-wa-emerg-badge">Bomberos 132</span>
                <span className="l-wa-emerg-badge">CONAF 130</span>
                <span className="l-wa-emerg-badge">Carabineros 133</span>
              </div>
              <div className="l-wa-detalle-firma">Montaraz · Guardián del territorio</div>
            </div>
          </div>

          </div>
          <p className="l-map-note" style={{ textAlign: 'center', marginTop: 20, paddingBottom: 8 }}>* Datos de demostración — en la app verás focos reales actualizados cada 15 min</p>
        </div>
      </section>

      {/* ── PARA QUIÉN ES ── */}
      <section className="l-section l-segmentos-section">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2>¿Para quién es Montaraz?</h2>
            <p>Para cualquiera que tenga algo que perder en un incendio</p>
          </div>
          <div className="l-segmentos-grid">
            {SEGMENTOS.map(s => (
              <div key={s.titulo} className="l-segmento-card l-reveal">
                <div className="l-segmento-icon">{s.icon}</div>
                <div className="l-segmento-titulo">{s.titulo}</div>
                <div className="l-segmento-dolor">{s.dolor}</div>
                <p className="l-segmento-desc">{s.desc}</p>
                <button
                  className="l-btn l-btn-ghost l-btn-pill"
                  style={{ marginTop: 'auto', fontSize: '.78rem' }}
                  onClick={() => openModal('registro', '', s.accion === 'empresa' ? 'empresa' : null)}
                >
                  {s.cta} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IMPACTO SOCIAL ── */}
      <section className="l-section l-impacto">
        <div className="l-container">
          <div className="l-impacto-inner">
            <div className="l-impacto-text">
              <div className="l-eyebrow l-reveal" style={{ textAlign: 'left' }}>Misión</div>
              <h2 className="l-reveal l-reveal--d1" style={{ fontSize: 'clamp(1.3rem,3vw,1.9rem)', fontWeight: 800, color: '#f0ece0', lineHeight: 1.2, margin: '.5rem 0 1rem' }}>
                Chile pierde miles de hectáreas cada verano. Montaraz existe para cambiar eso.
              </h2>
              <p className="l-reveal l-reveal--d2" style={{ color: 'rgba(240,236,224,.8)', fontSize: '.92rem', lineHeight: 1.75, marginBottom: '1.2rem' }}>
                Cada incendio que se detecta a tiempo es un bosque que se salva, una comunidad que evacúa con calma, un territorio que se recupera. Por eso Montaraz tiene también un programa de alertas gratuito para comunidades y juntas de vecinos en sectores de alta vulnerabilidad — cerros de la Región Metropolitana, interfaz urbano-rural — donde el riesgo es alto y los recursos, bajos.
              </p>
              <p className="l-reveal l-reveal--d3" style={{ color: 'rgba(240,236,224,.7)', fontSize: '.85rem', lineHeight: 1.7 }}>
                Si representas a una comunidad rural vulnerable y quieres postular, escríbenos a <a href="mailto:contacto@montaraz.cl" style={{ color: '#a8c97a', textDecoration: 'none' }}>contacto@montaraz.cl</a>.
              </p>
            </div>
            <div className="l-impacto-stats">
              <div className="l-impacto-stat">
                <div className="l-impacto-n">24/7</div>
                <div className="l-impacto-l">Monitoreo activo sin interrupciones</div>
              </div>
              <div className="l-impacto-stat">
                <div className="l-impacto-n">2</div>
                <div className="l-impacto-l">Fuentes satelitales independientes</div>
              </div>
              <div className="l-impacto-stat">
                <div className="l-impacto-n">0</div>
                <div className="l-impacto-l">Instalaciones necesarias</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="l-section l-planes" id="l-pricing">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2>Precios simples y sin letra chica</h2>
            <p>Un solo plan. Mismo precio para cualquier parcela o fundo de hasta 8 hectáreas. Sin letra chica.</p>
          </div>

          <div className="l-planes3-grid">

            {/* Plan Mensual */}
            <div className="l-plan3-card l-plan3-featured l-reveal" style={{ gridColumn: '1 / span 2' }}>
              <div className="l-plan3-nombre">Plan Mensual</div>
              <div className="l-plan3-precio">
                ${PRECIO_MENSUAL.toLocaleString('es-CL')}<span style={{ fontSize: 15, fontWeight: 600 }}>/mes</span>
              </div>
              <div className="l-plan3-sub">IVA incluido · cancela cuando quieras</div>
              <ul className="l-plan3-lista" style={{ marginTop: 12, marginBottom: 16 }}>
                <li>✓ Alertas por WhatsApp en tiempo real</li>
                <li>✓ Hasta 3 números de contacto</li>
                <li>✓ Monitoreo 24/7 con GOES + FIRMS</li>
                <li>✓ Ubicación exacta del foco y nivel de riesgo</li>
                <li>✓ Números de emergencia en cada alerta</li>
              </ul>
              <div style={{ fontSize: 11.5, color: '#8b7355', lineHeight: 1.5, marginBottom: 14 }}>
                Para propiedades de más de 8 ha necesitas varias zonas de monitoreo —{' '}
                <a href="mailto:contacto@montaraz.cl" style={{ color: '#15704f', fontWeight: 600 }}>escríbenos</a>{' '}
                para un plan a medida.
              </div>
              <button
                className="l-btn l-btn-primary l-btn-pill l-btn-submit"
                onClick={() => openModal('registro')}
              >
                Proteger mi terreno
              </button>
            </div>

            {/* Plan Institucional B2G */}
            <div className="l-plan3-card l-plan3-institucional l-reveal l-reveal--d2">
              <div className="l-plan-badge" style={{ background: 'rgba(255,255,255,.15)', color: '#f0ece0' }}>Municipios · CONAF · Bomberos</div>
              <div className="l-plan3-nombre" style={{ color: '#f0ece0' }}>Plan Institucional</div>
              <div className="l-plan3-precio" style={{ color: '#a8c97a' }}>
                A medida
              </div>
              <div className="l-plan3-sub" style={{ color: 'rgba(240,236,224,.7)' }}>cobertura comunal o regional</div>
              <ul className="l-plan3-lista" style={{ color: 'rgba(240,236,224,.85)' }}>
                <li>✓ Cobertura de zona completa</li>
                <li>✓ Integración con protocolos de emergencia</li>
                <li>✓ Panel de monitoreo en tiempo real</li>
                <li>✓ Reportes para gestión</li>
                <li>✓ Programa de alertas comunitarias incluido</li>
              </ul>
              <button
                className="l-btn l-btn-pill l-btn-submit"
                style={{ background: '#a8c97a', color: '#1a3a10' }}
                onClick={() => openModal('registro', '', 'empresa')}
              >
                Agendar reunión
              </button>
              <div className="l-plan3-nota" style={{ color: 'rgba(240,236,224,.6)' }}>Sin costo de evaluación</div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="l-section l-faq-section">
        <div className="l-container">
          <div className="l-section-header l-reveal">
            <h2>Preguntas frecuentes</h2>
            <p>Lo que más nos preguntan antes de suscribirse</p>
          </div>
          <div className="l-faq-lista">
            {FAQS.map((f, i) => (
              <details key={i} className="l-faq-item">
                <summary className="l-faq-pregunta">{f.q}</summary>
                <p className="l-faq-respuesta">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="l-cta-final">
        <div className="l-container" style={{ textAlign: 'center' }}>
          <h2 className="l-cta-final-h2">Duerme tranquilo esta noche.</h2>
          <p className="l-cta-final-sub">
            Mientras descansas, dos satélites siguen mirando. Si algo se mueve, te avisamos.
          </p>
          <div className="l-hero-cta" style={{ justifyContent: 'center', marginTop: '1.8rem' }}>
            <button className="l-btn l-btn-primary l-btn-pill l-btn-lg" onClick={() => openModal('registro')}>
              Proteger mi terreno ahora
            </button>
            <button className="l-btn l-btn-ghost l-btn-pill l-btn-lg" onClick={() => openModal('registro', '', 'empresa')}>
              Soy empresa o institución
            </button>
          </div>
          <p style={{ marginTop: '1.5rem', fontSize: '.8rem', color: '#8b7355' }}>
            Plan Mensual ${PRECIO_MENSUAL.toLocaleString('es-CL')} — IVA incluido, cancela cuando quieras.
          </p>
        </div>
      </section>

      {/* ── MODAL ── */}
      {modalOpen && (
        <div className="l-overlay" role="presentation" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div
            ref={modalRef}
            className="l-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Acceso a Montaraz"
            style={{ maxWidth: paso === 'terreno' ? 520 : 390 }}
          >
            <button className="l-modal-close" aria-label="Cerrar" onClick={closeModal}>✕</button>

            {/* ── SELECTOR TIPO CUENTA ── */}
            {tab === 'registro' && tipoCuenta === null && (
              <div style={{ textAlign: 'center' }}>
                <img src={logoHeaderHorizontal} alt="Montaraz" className="l-modal-logo" />
                <div className="l-modal-tagline">Guardián del Territorio</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#2a1f0f', marginBottom: 6, marginTop: 16 }}>¿Cómo quieres proteger tu terreno?</div>
                <div style={{ fontSize: 12, color: '#8b7355', marginBottom: 20 }}>El plan varía según el tipo de cuenta</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                  <button onClick={() => setTipoCuenta('personal')} style={{ border: '2px solid #d4c9a8', borderRadius: 12, padding: '14px 6px', cursor: 'pointer', background: '#faf7f0', textAlign: 'center', fontFamily: 'Inter, sans-serif', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#15704f'} onMouseLeave={e => e.currentTarget.style.borderColor = '#d4c9a8'}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>🏡</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#2a1f0f', marginBottom: 4 }}>Persona natural</div>
                    <div style={{ fontSize: 10, color: '#8b7355', lineHeight: 1.4 }}>Parcela, fundo<br />o terreno propio</div>
                  </button>
                  <button onClick={() => setTipoCuenta('comunal')} style={{ border: '2px solid #d4c9a8', borderRadius: 12, padding: '14px 6px', cursor: 'pointer', background: '#faf7f0', textAlign: 'center', fontFamily: 'Inter, sans-serif', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#15704f'} onMouseLeave={e => e.currentTarget.style.borderColor = '#d4c9a8'}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>🏘️</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#2a1f0f', marginBottom: 4 }}>Comunidad</div>
                    <div style={{ fontSize: 10, color: '#8b7355', lineHeight: 1.4 }}>Condominio o<br />junta de vecinos</div>
                  </button>
                  <button onClick={() => setTipoCuenta('empresa')} style={{ border: '2px solid #d4c9a8', borderRadius: 12, padding: '14px 6px', cursor: 'pointer', background: '#faf7f0', textAlign: 'center', fontFamily: 'Inter, sans-serif', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#15704f'} onMouseLeave={e => e.currentTarget.style.borderColor = '#d4c9a8'}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>🏢</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#2a1f0f', marginBottom: 4 }}>Empresa</div>
                    <div style={{ fontSize: 10, color: '#8b7355', lineHeight: 1.4 }}>Agrícola, forestal<br />ganadera o minera</div>
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#8b7355' }}>
                  ¿Ya tienes cuenta?{' '}
                  <button onClick={() => { setTab('login'); setTipoCuenta('personal'); }} style={{ background: 'none', border: 'none', color: '#15704f', cursor: 'pointer', fontWeight: 600, fontSize: 11, fontFamily: 'Inter, sans-serif', padding: 0 }}>Ingresar</button>
                </div>
              </div>
            )}

            {/* ── FORMULARIO EMPRESA ── */}
            {tab === 'registro' && tipoCuenta === 'empresa' && (
              <div>
                <button onClick={() => setTipoCuenta(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b7355', fontSize: 12, marginBottom: 14, padding: 0, fontFamily: 'Inter, sans-serif' }}>← Volver</button>
                {empresaEnviado ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 700, color: '#15704f', fontSize: 15, marginBottom: 8 }}>¡Listo! Revisa tu correo</div>
                    <div style={{ fontSize: 13, color: '#4a3f2a', lineHeight: 1.7, marginBottom: 10 }}>
                      Te enviamos un link a <strong>{empresaForm.email}</strong> para establecer tu contraseña y acceder al dashboard.
                    </div>
                    <div style={{ fontSize: 12, color: '#8b7355', lineHeight: 1.6 }}>
                      Podrás explorar la plataforma libremente. Nos contactamos en menos de 24 horas con tu propuesta.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#2a1f0f', marginBottom: 4 }}>Solución empresarial</div>
                    <div style={{ fontSize: 12, color: '#8b7355', marginBottom: 16, lineHeight: 1.6 }}>
                      Múltiples predios, varios contactos por alerta, factura y contrato anual. Te preparamos una propuesta a medida.
                    </div>
                    <input
                      type="text"
                      name="pagina_web"
                      value={empresaForm.pagina_web}
                      onChange={e => setEmpresaForm({ ...empresaForm, pagina_web: e.target.value })}
                      tabIndex="-1"
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="l-field"><input placeholder="Razón social o nombre empresa" value={empresaForm.razon} onChange={e => setEmpresaForm({ ...empresaForm, razon: e.target.value })} /></div>
                    <div className="l-field"><input placeholder="Nombre de contacto" value={empresaForm.contacto} onChange={e => setEmpresaForm({ ...empresaForm, contacto: e.target.value })} /></div>
                    <div className="l-field"><input type="email" placeholder="Email" value={empresaForm.email} onChange={e => setEmpresaForm({ ...empresaForm, email: e.target.value })} /></div>
                    <div className="l-field"><input type="tel" placeholder="Teléfono (+56 9...)" value={empresaForm.telefono} onChange={e => setEmpresaForm({ ...empresaForm, telefono: e.target.value })} /></div>
                    <div className="l-field"><input type="number" placeholder="Hectáreas aproximadas a monitorear (opcional)" min="1" value={empresaForm.ha} onChange={e => setEmpresaForm({ ...empresaForm, ha: e.target.value })} /></div>
                    {error && <div className="l-form-err">{error}</div>}
                    <button
                      className="l-btn l-btn-primary l-btn-pill l-btn-submit"
                      disabled={cargando}
                      onClick={async () => {
                        if (!empresaForm.razon || !empresaForm.contacto || !empresaForm.email || !empresaForm.telefono) return setError('Completa todos los campos.');
                        setCargando(true); setError('');
                        try {
                          await fetch(`${API}/api/auth/contacto-empresa`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(empresaForm),
                          });
                          setEmpresaEnviado(true);
                        } catch { setError('Error de conexión'); }
                        finally { setCargando(false); }
                      }}
                    >
                      {cargando ? 'Enviando...' : 'Solicitar propuesta'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Indicador de pasos (solo en registro personal) */}
            {(tipoCuenta === 'personal' || tipoCuenta === 'comunal' || tab === 'login') && paso !== 'auth' && (
              <div className="l-pasos">
                {['Cuenta', 'Terreno'].map((s, i) => {
                  const idx = paso === 'auth' ? 0 : 1;
                  return (
                    <div key={s} className={`l-paso${i < idx ? ' done' : i === idx ? ' active' : ''}`}>
                      <div className="l-paso-dot">{i < idx ? '✓' : i + 1}</div>
                      <div className="l-paso-label">{s}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── PASO AUTH (login / registro personal) ── */}
            {paso === 'auth' && (tipoCuenta === 'personal' || tipoCuenta === 'comunal' || tab === 'login') && (
              <>
                <img src={logoHeaderHorizontal} alt="Montaraz" className="l-modal-logo" />
                <div className="l-modal-tagline">Guardián del Territorio</div>
                {tab !== 'recuperar' && (
                  <div className="l-seg">
                    <button className={`l-seg-btn${tab === 'login' ? ' active' : ''}`} onClick={() => { setTab('login'); setError(''); }}>Ingresar</button>
                    <button className={`l-seg-btn${tab === 'registro' ? ' active' : ''}`} onClick={() => { setTab('registro'); setError(''); }}>Registrarse</button>
                  </div>
                )}
                {authOk ? (
                  <div className="l-auth-ok">✓ Listo — abriendo el mapa...</div>
                ) : tab === 'recuperar' ? (
                  <div>
                    <button onClick={() => { setTab('login'); setRecuperarOk(false); setError(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b7355', fontSize: 13, marginBottom: 14, padding: 0 }}>
                      ← Volver
                    </button>
                    {recuperarOk ? (
                      <div style={{ textAlign: 'center', padding: '10px 0' }}>
                        <div style={{ fontSize: 32, marginBottom: 10 }}>📬</div>
                        <div style={{ fontWeight: 700, color: '#15704f', marginBottom: 6 }}>Email enviado</div>
                        <div style={{ fontSize: 13, color: '#8b7355', lineHeight: 1.6 }}>Revisa tu bandeja y sigue el link. Expira en 1 hora.</div>
                      </div>
                    ) : (
                      <form onSubmit={handleRecuperar}>
                        <div style={{ fontSize: 13, color: '#4a3f2a', marginBottom: 14, lineHeight: 1.6 }}>Ingresa tu email y te enviaremos un link para restablecer tu contraseña.</div>
                        <div className="l-field"><input type="email" required placeholder="Tu email" value={recuperarEmail} onChange={e => setRecuperarEmail(e.target.value)} /></div>
                        {error && <div className="l-form-err">{error}</div>}
                        <button type="submit" className="l-btn l-btn-primary l-btn-pill l-btn-submit" disabled={cargando}>{cargando ? 'Enviando...' : 'Enviar link de recuperación'}</button>
                      </form>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {tab === 'registro' && (
                      <div className="l-field"><input type="text" required placeholder="Nombre completo" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
                    )}
                    <div className="l-field"><input type="email" required placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                    <div className="l-field">
                      <input type="password" required placeholder="Contraseña" minLength={tab === 'registro' ? 8 : undefined} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                    </div>
                    {tab === 'registro' && (
                      <div className="l-tel-group">
                        <select
                          className="l-tel-pais"
                          value={pPais}
                          onChange={e => { setPPais(e.target.value); setPTel(''); }}
                        >
                          {Object.entries(PAISES).map(([k, p]) => (
                            <option key={k} value={k}>{p.bandera} {p.code}</option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          required
                          className="l-field l-tel-numero"
                          placeholder={pPais === 'CL' ? '9 1234 5678' : '11 1234 5678'}
                          value={pTel}
                          onChange={e => setPTel(formatTelLocal(e.target.value, pPais))}
                        />
                      </div>
                    )}
                    {tab === 'registro' && (
                      <div style={{ fontSize: 11, color: '#8b7355', marginTop: -6, marginBottom: 10 }}>
                        Este es el WhatsApp donde recibirás las alertas de incendio.
                      </div>
                    )}
                    {tab === 'login' && (
                      <div style={{ textAlign: 'right', marginBottom: 10, marginTop: -4 }}>
                        <button type="button" onClick={() => { setTab('recuperar'); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b7355', fontSize: 12, padding: 0, fontFamily: 'Inter, sans-serif' }}>¿Olvidaste tu contraseña?</button>
                      </div>
                    )}
                    {tab === 'registro' && (
                      <label className="l-terminos-check">
                        <input
                          type="checkbox"
                          checked={regAceptaTerminos}
                          onChange={e => setRegAceptaTerminos(e.target.checked)}
                        />
                        <span>
                          Acepto los{' '}
                          <button type="button" className="l-terminos-link" onClick={() => setShowTerminos(true)}>
                            Términos y Condiciones
                          </button>
                          {' '}del servicio
                        </span>
                      </label>
                    )}
                    {error && <div className="l-form-err">{error}</div>}
                    <button type="submit" className="l-btn l-btn-primary l-btn-pill l-btn-submit" disabled={cargando}>
                      {cargando ? 'Cargando...' : tab === 'login' ? 'Ingresar' : 'Continuar →'}
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── PASO 2: TERRENO ── */}
            {paso === 'terreno' && (tipoCuenta === 'personal' || tipoCuenta === 'comunal') && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#2a1f0f', marginBottom: 4 }}>
                  {tipoCuenta === 'comunal' ? 'Tu zona comunitaria' : 'Tu terreno'}
                </div>
                <div style={{ fontSize: 12, color: '#8b7355', marginBottom: 14 }}>
                  {tipoCuenta === 'comunal'
                    ? 'Dibuja el contorno que cubre a tu comunidad (condominio, loteo o sector) en el mapa.'
                    : 'Dibuja el contorno de tu propiedad en el mapa.'}
                </div>
                <div className="l-field">
                  <input
                    placeholder={tipoCuenta === 'comunal' ? 'Nombre de la comunidad (ej: Condominio Los Aromos)' : 'Nombre del terreno (ej: Fundo Los Boldos)'}
                    value={pNombre} onChange={e => setPNombre(e.target.value)}
                  />
                </div>

                <DibujarPredio
                  onChange={({ poligono, superaMax }) => { setPPoligono(poligono); setPSuperaMax(superaMax); }}
                  maxHa={tipoCuenta === 'comunal' ? 50 : 8}
                />

                {pPoligono && !pSuperaMax && (
                  tipoCuenta === 'comunal' ? (
                    <div style={{ background: '#eef5e8', border: '1px solid #d4c9a8', borderRadius: 9, padding: '8px 12px', margin: '4px 0 10px', fontSize: 11.5, color: '#8b7355', lineHeight: 1.5 }}>
                      El precio del Plan Comunal depende de cuántas viviendas se sumen — lo ves en el siguiente paso, junto con dónde agregar a tus vecinos.
                    </div>
                  ) : (
                    <div style={{ background: '#eef5e8', border: '1px solid #d4c9a8', borderRadius: 9, padding: '8px 12px', margin: '4px 0 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#8b7355' }}>Plan Mensual · cancela cuando quieras</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#15704f' }}>${PRECIO_MENSUAL.toLocaleString('es-CL')}/mes</span>
                    </div>
                  )
                )}
                <label className="l-terminos-check">
                  <input
                    type="checkbox"
                    checked={pAceptaTerminos}
                    onChange={e => setPAceptaTerminos(e.target.checked)}
                  />
                  <span>
                    Acepto los{' '}
                    <button type="button" className="l-terminos-link" onClick={() => setShowTerminos(true)}>
                      Términos y Condiciones
                    </button>
                    {' '}del servicio
                  </span>
                </label>
                {error && <div className="l-form-err">{error}</div>}
                <button onClick={handleCrearPredio} disabled={cargando || !pPoligono || pSuperaMax} className="l-btn l-btn-primary l-btn-pill l-btn-submit" style={{ opacity: (cargando || !pPoligono || pSuperaMax) ? 0.6 : 1 }}>
                  {cargando ? 'Guardando...' : 'Entrar al dashboard →'}
                </button>
              </div>
            )}

            <div className="l-modal-foot">Protegiendo el territorio desde el espacio</div>
          </div>
        </div>
      )}

      {/* ── FORMULARIO DE CONTACTO ── */}
      <section className="l-section l-contacto-section" id="l-contacto">
        <div className="l-container">
          <div className="l-contacto-inner">
            <div className="l-contacto-info">
              <div className="l-eyebrow">Contacto</div>
              <h2 className="l-contacto-h2">¿Tienes dudas o quieres una propuesta?</h2>
              <p className="l-contacto-sub">
                Escríbenos y te respondemos en menos de 24 horas. Para empresas, municipios y proyectos especiales preparamos una propuesta a medida.
              </p>
              <div className="l-contacto-datos">
                <div className="l-contacto-dato">
                  <span>📧</span>
                  <a href="mailto:contacto@montaraz.cl">contacto@montaraz.cl</a>
                </div>
                <div className="l-contacto-dato">
                  <span>📍</span>
                  <span>Chile</span>
                </div>
              </div>
            </div>

            <div className="l-contacto-form-wrap">
              {ctEnviado ? (
                <div className="l-contacto-ok">
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📬</div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>¡Mensaje recibido!</div>
                  <p style={{ color: '#8b7355', fontSize: 13, lineHeight: 1.6 }}>
                    Te respondemos en menos de 24 horas a <strong>{ctForm.email}</strong>.
                  </p>
                </div>
              ) : (
                <form className="l-contacto-form" onSubmit={handleContacto} noValidate>
                  {/* Honeypot anti-spam: invisible para una persona, un bot que autocompleta
                      todos los inputs de un form lo llena. Si viene con contenido, el backend
                      descarta el envío en silencio. No es type="hidden" a propósito. */}
                  <input
                    type="text"
                    name="pagina_web"
                    value={ctForm.pagina_web}
                    onChange={e => setCtForm({ ...ctForm, pagina_web: e.target.value })}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  />
                  <div className="l-ct-row">
                    <div className="l-ct-field">
                      <label>Nombre *</label>
                      <input
                        type="text" placeholder="Tu nombre"
                        value={ctForm.nombre}
                        onChange={e => setCtForm({ ...ctForm, nombre: e.target.value })}
                      />
                    </div>
                    <div className="l-ct-field">
                      <label>Email *</label>
                      <input
                        type="email" placeholder="tu@email.com"
                        value={ctForm.email}
                        onChange={e => setCtForm({ ...ctForm, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="l-ct-row">
                    <div className="l-ct-field">
                      <label>Teléfono</label>
                      <input
                        type="tel" placeholder="+56 9 1234 5678"
                        value={ctForm.telefono}
                        onChange={e => setCtForm({ ...ctForm, telefono: formatTelLocal(e.target.value, 'CL') })}
                      />
                    </div>
                    <div className="l-ct-field">
                      <label>Tipo de consulta</label>
                      <select
                        value={ctForm.tipo}
                        onChange={e => setCtForm({ ...ctForm, tipo: e.target.value })}
                      >
                        <option value="">Selecciona...</option>
                        <option value="persona">Persona natural / parcela</option>
                        <option value="empresa">Empresa agrícola / forestal</option>
                        <option value="municipio">Municipalidad / institución</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  <div className="l-ct-field">
                    <label>Mensaje *</label>
                    <textarea
                      placeholder="Cuéntanos tu caso, la superficie aproximada o lo que necesitas saber..."
                      rows={4}
                      value={ctForm.mensaje}
                      onChange={e => setCtForm({ ...ctForm, mensaje: e.target.value })}
                    />
                  </div>
                  {ctError && <div className="l-form-err">{ctError}</div>}
                  <button type="submit" className="l-btn l-btn-primary l-btn-pill" style={{ padding: '.7rem 2rem', fontSize: '.9rem' }} disabled={ctCargando}>
                    {ctCargando ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="l-footer">
        <div className="l-footer-brand">
          <img src={logoHeaderWhite} alt="Montaraz" className="l-footer-logo" />
        </div>
        <div className="l-footer-social">
          <a href="https://instagram.com/montaraz.app" target="_blank" rel="noopener noreferrer" className="l-social-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
            </svg>
            @montaraz.app
          </a>
        </div>
        <p>© 2026 Montaraz · Guardián del Territorio</p>
        <p style={{ marginTop: '.3rem' }}>
          <button className="l-footer-link" onClick={() => openModal('login')}>Abrir la app</button>
          {' · '}
          <a href="mailto:contacto@montaraz.cl">contacto@montaraz.cl</a>
          {' · '}
          <button className="l-footer-link" onClick={() => setShowTerminos(true)}>Términos y Condiciones</button>
        </p>
      </footer>

      {/* ── MODAL TÉRMINOS Y CONDICIONES ── */}
      {showTerminos && (
        <div className="l-overlay" onClick={() => setShowTerminos(false)} style={{ zIndex: 2000 }}>
          <div className="l-terminos-modal" onClick={e => e.stopPropagation()}>
            <div className="l-terminos-header">
              <h2 className="l-terminos-titulo">Términos y Condiciones</h2>
              <button className="l-modal-close" aria-label="Cerrar términos" onClick={() => setShowTerminos(false)}>×</button>
            </div>
            <div className="l-terminos-body">
              <p className="l-terminos-fecha">Última actualización: junio de 2026</p>

              <h3>1. Descripción del servicio</h3>
              <p>Montaraz es un sistema de monitoreo y alerta temprana de incendios forestales que utiliza datos satelitales para detectar focos de calor y notificar a los usuarios mediante mensajes de WhatsApp. El servicio es complementario y no reemplaza a los organismos oficiales de emergencia.</p>

              <h3>2. Registro y cuenta</h3>
              <p>Para acceder al servicio debes registrar una cuenta con información verídica. Eres responsable de mantener la confidencialidad de tus credenciales y de todas las actividades realizadas bajo tu cuenta.</p>

              <h3>3. Uso del número de teléfono</h3>
              <p>Al proporcionar tu número de teléfono, autorizas expresamente a Montaraz a enviarte mensajes de WhatsApp con alertas de incendios, notificaciones del servicio e información relevante para la seguridad de tus predios. Puedes solicitar la baja de las notificaciones en cualquier momento contactando a <a href="mailto:contacto@montaraz.cl">contacto@montaraz.cl</a>.</p>

              <h3>4. Planes y pagos</h3>
              <p>Montaraz ofrece un único Plan Mensual de $13.900 (IVA incluido, en pesos chilenos), con renovación automática cada mes. El cliente puede cancelar la renovación en cualquier momento desde su perfil, sin permanencia ni penalización; la cobertura se mantiene hasta el término del período ya pagado. El pago es procesado de forma segura por Flow y Montaraz no almacena los datos de la tarjeta.</p>
              <p>El Plan Mensual cubre <strong>una (1) propiedad de uso residencial o agrícola menor, de hasta 8 hectáreas</strong>. Propiedades de mayor superficie, plantaciones forestales o el monitoreo de varios predios requieren un plan Empresa a medida, que se contrata escribiendo a contacto@montaraz.cl. El cliente declara la superficie real de su propiedad; declarar una superficie menor a la real deja parte del predio fuera del área de cobertura y sin alertas. Montaraz puede suspender la cobertura si detecta un uso que no corresponde a las condiciones de este plan.</p>

              <h3>5. Limitación de responsabilidad</h3>
              <p>Montaraz utiliza fuentes satelitales de terceros (NASA FIRMS, GOES) para la detección de focos, junto con servicios de mensajería de terceros para el envío de alertas por WhatsApp. Trabajamos permanentemente en la mejora continua del sistema para aumentar su precisión y velocidad de respuesta; sin embargo, ningún sistema de detección o notificación satelital puede garantizar un funcionamiento libre de errores.</p>
              <p>Existen factores fuera de nuestro control que pueden afectar el servicio — entre ellos, interrupciones o degradación en la señal celular o de internet del destinatario, fallas o demoras en los servicios de mensajería de terceros, condiciones climáticas que reduzcan la cobertura satelital, o mantenimientos de las plataformas involucradas. Cualquiera de estos factores puede ocasionar un retraso en la detección o el envío de una alerta, o que esta no llegue a destino. Al contratar el servicio, el usuario reconoce y acepta esta posibilidad.</p>
              <p>Montaraz es una herramienta adicional de apoyo a la prevención y no reemplaza el autocuidado, los sistemas oficiales de emergencia ni el criterio del usuario frente a una situación de riesgo. Montaraz no garantiza la detección del 100% de los focos y no es responsable por daños directos, indirectos o consecuenciales derivados de demoras, fallas de envío o inexactitudes en las alertas. Ante cualquier señal de riesgo, actúa con precaución y contacta de inmediato a Bomberos (132), CONAF (130) o Carabineros (133) — nunca dependas exclusivamente de una alerta automática.</p>

              <h3>6. Privacidad de datos</h3>
              <p>Los datos personales recopilados (nombre, email, teléfono, ubicación de predios) se utilizan exclusivamente para la prestación del servicio. No se comparten con terceros con fines comerciales. Para más información, contáctanos en <a href="mailto:contacto@montaraz.cl">contacto@montaraz.cl</a>.</p>

              <h3>7. Modificaciones</h3>
              <p>Montaraz se reserva el derecho de modificar estos términos. Los cambios serán comunicados por email con al menos 15 días de anticipación. El uso continuado del servicio implica aceptación de los nuevos términos.</p>

              <h3>8. Legislación aplicable</h3>
              <p>Estos términos se rigen por la legislación chilena. Cualquier disputa será sometida a los tribunales ordinarios de justicia de la República de Chile.</p>

              <p style={{ marginTop: 20, color: '#8b7355', fontSize: 12 }}>Para consultas sobre estos términos, escríbenos a <a href="mailto:contacto@montaraz.cl">contacto@montaraz.cl</a>.</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
